import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Lottie from 'lottie-react';
import alprestamoIcon from '../assets/alprestamo.png';
import prestixIcon from '../assets/isologo-cropped.svg';
import arrowAnimation from '../assets/arrow.json';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
`;

const IconCircle = styled.div<{ isActive?: boolean; isCompleted?: boolean; isAlprestamo?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.isCompleted) return '#0166FF';
    if (props.isActive && props.isAlprestamo) return '#0166FF';
    if (props.isActive && !props.isAlprestamo) return '#fff000';
    return '#f5f5f5';
  }};
  border: 3px solid ${props => {
    if (props.isCompleted) return '#0166FF';
    if (props.isActive && props.isAlprestamo) return '#0166FF';
    if (props.isActive && !props.isAlprestamo) return '#000';
    return '#ddd';
  }};
  transition: all 0.5s ease;
  animation: ${props => props.isActive ? pulse : 'none'} 2s infinite;
  box-shadow: ${props => {
    if (props.isCompleted) return '0 4px 12px rgba(1, 102, 255, 0.3)';
    if (props.isActive && props.isAlprestamo) return '0 4px 12px rgba(1, 102, 255, 0.3)';
    if (props.isActive && !props.isAlprestamo) return '0 4px 12px rgba(255, 240, 0, 0.3)';
    return '0 2px 8px rgba(0, 0, 0, 0.1)';
  }};
  position: relative;
`;

const Icon = styled.img<{ $isCompleted?: boolean }>`
  width: 35px;
  height: 35px;
  object-fit: contain;
  filter: ${props => props.$isCompleted ? 'brightness(0) invert(1)' : 'none'};
`;

const ArrowContainer = styled.div`
  position: relative;
  width: 80px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LottieArrow = styled.div<{ isActive?: boolean }>`
  width: 70px;
  height: auto;
  opacity: ${props => props.isActive ? 1 : 0.3};
  transition: opacity 0.3s ease;
`;

const Label = styled.div<{ isActive?: boolean; isCompleted?: boolean; isAlprestamo?: boolean }>`
  font-size: 12px;
  font-weight: ${props => {
    if (props.isCompleted) return '600';
    if (props.isActive) return '600';
    return '400';
  }};
  color: ${props => {
    if (props.isCompleted) return '#0166FF';
    if (props.isActive && props.isAlprestamo) return '#0166FF';
    if (props.isActive && !props.isAlprestamo) return '#000';
    return '#666';
  }};
  text-align: center;
  margin-top: 8px;
  transition: all 0.3s ease;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const Checkmark = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  background: #0166FF;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
  animation: ${fadeIn} 0.3s ease-out;
`;

interface TransferIndicatorProps {
  currentStep: 'alprestamo' | 'prestix';
}

const TransferIndicator: React.FC<TransferIndicatorProps> = ({ 
  currentStep
}) => {
  return (
    <Container>
      <IconWrapper>
        <IconCircle 
          isActive={currentStep === 'alprestamo'} 
          isCompleted={currentStep === 'prestix'}
          isAlprestamo={true}
        >
          <Icon 
            src={alprestamoIcon} 
            alt="AlPréstamo" 
            $isCompleted={currentStep === 'prestix'}
          />
        </IconCircle>
      </IconWrapper>
      
      <ArrowContainer>
        <LottieArrow isActive={currentStep === 'prestix'}>
          <Lottie 
            animationData={arrowAnimation}
            loop={currentStep === 'prestix'}
            autoplay={currentStep === 'prestix'}
            style={{ width: '100%', height: '100%' }}
          />
        </LottieArrow>
      </ArrowContainer>
      
      <IconWrapper>
        <IconCircle isActive={currentStep === 'prestix'} isAlprestamo={false}>
          <Icon src={prestixIcon} alt="Prestix" />
        </IconCircle>
      </IconWrapper>
    </Container>
  );
};

export default TransferIndicator;
